<template>
  <component
    :is="embedded ? 'div' : 'v-dialog'"
    v-bind="shellProps"
    @input="onShellInput"
  >
    <v-card :flat="embedded">
      <v-card-title v-if="!embedded" class="dialog-header py-2">
        <v-icon class="mr-3 pt-1" color="white" size="24">mdi-routes</v-icon>
        <span class="dialog-title">Routes</span>
        <v-spacer></v-spacer>
        <v-btn class="white--text my-0" icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs
        v-if="!embedded && canManagePresets"
        v-model="tab"
        color="transparent"
        slider-color="success"
      >
        <v-tab>Routes</v-tab>
        <v-tab>Colors</v-tab>
        <v-tab>Driver Notifications</v-tab>
      </v-tabs>

      <v-card-text class="pa-3 identity-body">
        <!-- Routes -->
        <div v-show="currentPanel === 0">
          <div class="caption grey--text mb-2">
            Set the room number and bus color for each bus. The color list
            itself is maintained by admin and Transportation.
          </div>
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 sm3 class="pr-2">
              <v-btn-toggle v-model="kind" mandatory class="identity-toggle">
                <v-btn flat small value="Regular">
                  <v-icon small class="mr-1">mdi-school</v-icon>Regular
                </v-btn>
                <v-btn flat small value="DayCamp">
                  <v-icon small class="mr-1">mdi-tent</v-icon>Day Camp
                </v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-model="building"
                :items="buildingItems"
                label="Building"
                dense
                hide-details
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-model="direction"
                :items="directionItems"
                label="Direction"
                dense
                hide-details
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                v-model="search"
                label="Search bus, room, grade"
                prepend-inner-icon="mdi-magnify"
                dense
                hide-details
                clearable
              ></v-text-field>
            </v-flex>
          </v-layout>
          <div v-if="!loading" class="caption grey--text mb-2">
            {{ visibleRoutes.length }}
            {{ visibleRoutes.length === 1 ? "route" : "routes" }}
          </div>

          <div v-if="loading" class="text-xs-center py-4 grey--text">
            Loading routes…
          </div>
          <div
            v-else-if="!visibleRoutes.length"
            class="text-xs-center py-4 grey--text"
          >
            No routes found.
          </div>
          <table v-else class="identity-table">
            <thead>
              <tr>
                <th class="col-tile"></th>
                <th>Bus</th>
                <th>Direction</th>
                <th>Time</th>
                <th>Grades</th>
                <th>Buildings</th>
                <th class="col-room">Room #</th>
                <th class="col-color">Bus Color</th>
                <th class="col-save"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRoutes"
                :key="row.id"
                :class="{ 'identity-row-editing': editingId === row.id }"
              >
                <td class="col-tile">
                  <div
                    class="identity-tile"
                    :class="{ 'identity-tile-mono': rowTile(row).mono }"
                    :style="rowTile(row).style"
                  >
                    {{ rowTile(row).room || rowTile(row).busNumber }}
                  </div>
                </td>
                <td>
                  <div class="font-weight-bold">#{{ row.busNumber || "—" }}</div>
                  <div class="caption grey--text">{{ row.driver }}</div>
                </td>
                <td>{{ row.direction }}</td>
                <td>{{ row.timeStart }}</td>
                <td class="caption">{{ row.grades }}</td>
                <td class="caption">{{ buildingLabel(row) }}</td>
                <template v-if="editingId === row.id">
                  <td class="col-room">
                    <div
                      v-for="b in row.identityRows"
                      :key="'room-' + b.building"
                      class="identity-building-line"
                    >
                      <span
                        v-if="showBuildingOnIdentity(row)"
                        class="identity-building-name"
                        >{{ b.building }}</span
                      >
                      <v-text-field
                        v-model="b.editRoom"
                        dense
                        hide-details
                        single-line
                        placeholder="Room"
                        maxlength="50"
                        @keyup.enter="saveRoute(row)"
                        @keyup.esc="cancelEdit(row)"
                      ></v-text-field>
                    </div>
                  </td>
                  <td class="col-color">
                    <div
                      v-for="b in row.identityRows"
                      :key="'color-' + b.building"
                      class="identity-building-line"
                    >
                      <v-autocomplete
                        v-model="b.editColor"
                        :items="colorItems"
                        item-text="name"
                        item-value="name"
                        dense
                        hide-details
                        single-line
                        clearable
                        placeholder="Type a color"
                        no-data-text="No matching color"
                      >
                        <template v-slot:selection="{ item }">
                          <span class="color-swatch mr-2" :style="{ background: item.hex }"></span>
                          {{ item.name }}
                        </template>
                        <template v-slot:item="{ item }">
                          <span class="color-swatch mr-2" :style="{ background: item.hex }"></span>
                          {{ item.name }}
                        </template>
                      </v-autocomplete>
                    </div>
                  </td>
                  <td class="col-save text-no-wrap">
                    <v-btn
                      small
                      color="success"
                      class="my-0"
                      :disabled="!isDirty(row)"
                      :loading="row.saving"
                      @click="saveRoute(row)"
                      >Save</v-btn
                    >
                    <v-btn icon small class="my-0" title="Cancel" @click="cancelEdit(row)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </template>
                <template v-else>
                  <td class="col-room">
                    <div
                      v-for="b in row.identityRows"
                      :key="'view-room-' + b.building"
                      class="identity-building-line"
                    >
                      <span
                        v-if="showBuildingOnIdentity(row)"
                        class="identity-building-name"
                        >{{ b.building }}</span
                      >
                      {{ b.roomNumber || "" }}
                    </div>
                  </td>
                  <td class="col-color">
                    <div
                      v-for="b in row.identityRows"
                      :key="'view-color-' + b.building"
                      class="identity-building-line"
                    >
                      <span v-if="b.chiderColor">
                        <span
                          class="color-swatch mr-2"
                          :style="{ background: colorMap[b.chiderColor.toUpperCase()] || '#fff' }"
                        ></span>
                        {{ b.chiderColor }}
                      </span>
                    </div>
                  </td>
                  <td class="col-save">
                    <v-btn icon small class="my-0" title="Edit" @click="startEdit(row)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Colors: admin and Transportation edit this list -->
        <div v-show="currentPanel === 1">
          <div :class="{ 'presets-locked': !canManagePresets }">
          <v-layout row align-center class="mb-3 color-add-row">
            <input v-model="newColor.hex" type="color" class="color-input mr-2" :disabled="!canManagePresets" />
            <v-text-field
              v-model="newColor.name"
              :disabled="!canManagePresets"
              label="New color name"
              dense
              hide-details
              class="mr-2"
              maxlength="50"
              @keyup.enter="addColor"
            ></v-text-field>
            <v-btn
              small
              color="success"
              :disabled="!canManagePresets || !newColor.name"
              :loading="addingColor"
              @click="addColor"
              >Add</v-btn
            >
          </v-layout>
          <div v-for="c in colors" :key="c.id" class="color-row">
            <input v-model="c.editHex" type="color" class="color-input mr-2" :disabled="!canManagePresets" />
            <v-text-field
              v-model="c.editName"
              :disabled="!canManagePresets"
              dense
              hide-details
              single-line
              class="mr-2"
              maxlength="50"
            ></v-text-field>
            <span class="caption grey--text mr-3 color-hex">{{ c.editHex }}</span>
            <v-btn
              v-if="c.editName !== c.name || c.editHex.toUpperCase() !== c.hex.toUpperCase()"
              small
              color="success"
              class="my-0"
              :loading="c.saving"
              @click="saveColor(c)"
              >Save</v-btn
            >
            <v-btn icon small class="my-0" title="Delete" :disabled="!canManagePresets" @click="deleteColor(c)">
              <v-icon small color="error">mdi-delete</v-icon>
            </v-btn>
          </div>
          </div>
        </div>

        <!-- Driver notification schedules: admin and Transportation -->
        <div v-show="currentPanel === 2">
          <div class="caption grey--text mb-2">
            Drivers get a notification with today's bus changes at "Send at".
            That covers the afternoon dropoff bus. An extended change that
            includes a morning pickup also notifies that pickup bus, when the
            route or its building has its own schedule. A route schedule beats
            a building schedule, and a building schedule beats the default.
            Bus changes entered after "Send at" still get a follow-up
            notification until "Keep sending until"; after that they wait for
            the next day.
            <span>Leave it blank to keep sending for the rest of the day.</span>
          </div>

          <div class="schedule-editor mb-3" :class="{ 'presets-locked': !canManagePresets }">
            <v-layout row wrap align-center>
              <v-flex xs12 sm3 class="pr-2">
                <v-select
                  v-model="scheduleForm.scope"
                  :items="scheduleScopeItems"
                  label="Applies to"
                  dense
                  hide-details
                ></v-select>
              </v-flex>
              <v-flex v-if="scheduleForm.scope === 'building'" xs12 sm3 class="pr-2">
                <v-select
                  v-model="scheduleForm.building"
                  :items="buildings"
                  label="Building"
                  dense
                  hide-details
                ></v-select>
              </v-flex>
              <v-flex v-if="scheduleForm.scope === 'route'" xs12 sm5 class="pr-2">
                <v-btn small outline color="success" class="ma-0" @click="openRoutePicker">
                  <v-icon small left>mdi-table-search</v-icon>
                  {{ selectedRouteItems.length ? "Change routes" : "Choose routes" }}
                </v-btn>
                <span v-if="selectedRouteItems.length" class="caption grey--text ml-2">
                  {{ selectedRouteItems.length }} selected
                </span>
              </v-flex>
              <v-flex v-if="scheduleForm.scope === 'route' && selectedRouteItems.length" xs12 class="mt-2">
                <v-chip
                  v-for="item in selectedRouteItems"
                  :key="item.value"
                  small
                  close
                  class="my-1"
                  @input="removeScheduleRoute(item.value)"
                >
                  #{{ item.busNumber }} · {{ item.direction }}
                  <span v-if="item.timeStart"> · {{ item.timeStart }}</span>
                  <span v-if="item.buildings"> · {{ item.buildings }}</span>
                </v-chip>
              </v-flex>
              <v-flex xs6 sm2 class="pr-2">
                <v-text-field
                  v-model="scheduleForm.sendTime"
                  type="time"
                  label="Send at"
                  dense
                  hide-details
                ></v-text-field>
              </v-flex>
              <v-flex xs6 sm2 class="pr-2">
                <v-text-field
                  v-model="scheduleForm.lastSendTime"
                  type="time"
                  label="Keep sending until"
                  dense
                  hide-details
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row wrap align-center class="mt-2">
              <div class="weekday-toggle mr-3">
                <button
                  v-for="d in weekDayItems"
                  :key="d.value"
                  type="button"
                  class="weekday-btn"
                  :class="{ 'weekday-btn-on': scheduleForm.weekDays.includes(d.value) }"
                  @click="toggleWeekDay(d.value)"
                >
                  {{ d.text }}
                </button>
              </div>
              <v-checkbox
                v-model="scheduleForm.isActive"
                label="Active"
                color="success"
                hide-details
                class="mt-0 pt-0 mr-3 schedule-active"
              ></v-checkbox>
              <v-text-field
                v-model="scheduleForm.notes"
                label="Notes"
                dense
                hide-details
                maxlength="200"
                class="mr-2"
              ></v-text-field>
              <v-btn
                small
                color="success"
                class="my-0"
                :disabled="!canManagePresets || !canSaveSchedule"
                :loading="savingSchedule"
                @click="saveSchedule"
                >{{ scheduleForm.id ? "Save" : "Add" }}</v-btn
              >
              <v-btn
                v-if="scheduleForm.id"
                small
                flat
                class="my-0"
                @click="resetScheduleForm"
                >Cancel</v-btn
              >
            </v-layout>
          </div>

          <v-dialog v-model="routePickerOpen" max-width="980" scrollable>
            <v-card class="route-picker-card">
              <v-card-title class="route-picker-title">
                <v-icon color="white" class="mr-3">mdi-bus</v-icon>
                <div>
                  <div class="route-picker-heading">Choose routes</div>
                  <div class="route-picker-subheading">
                    Select the routes this schedule applies to
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn icon small dark class="ma-0 white--text" @click="routePickerOpen = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <div class="route-picker-filters">
                <v-text-field
                  v-model="routePickerSearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search bus, building, grade, or time"
                  dense
                  hide-details
                  clearable
                  class="mr-3 route-picker-search"
                ></v-text-field>
                <v-checkbox
                  v-model="hideScheduledRoutes"
                  label="Hide routes with a schedule"
                  color="success"
                  hide-details
                  class="mt-0 pt-0 route-picker-hide"
                ></v-checkbox>
              </div>
              <v-card-text class="pa-0 route-picker-body">
                <table class="identity-table route-picker-table">
                  <thead>
                    <tr>
                      <th class="route-picker-check">
                        <v-checkbox
                          :input-value="allPickerRoutesSelected"
                          :disabled="!pickerRouteItems.length"
                          color="success"
                          hide-details
                          class="route-picker-cb"
                          @change="togglePickerAll"
                        ></v-checkbox>
                      </th>
                      <th v-for="col in pickerColumns" :key="col.key" class="route-picker-th">
                        <div class="route-picker-sort" @click="sortPicker(col.key)">
                          {{ col.label }}
                          <v-icon
                            small
                            class="route-picker-sort-icon"
                            :class="{ 'route-picker-sort-on': pickerSort.key === col.key }"
                            >{{
                              pickerSort.key === col.key && pickerSort.dir === "desc"
                                ? "mdi-arrow-down"
                                : "mdi-arrow-up"
                            }}</v-icon
                          >
                        </div>
                        <input
                          v-model="pickerColFilters[col.key]"
                          type="text"
                          class="route-picker-colfilter"
                          placeholder="Filter"
                          @click.stop
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in pickerRouteItems"
                      :key="r.value"
                      class="route-picker-row"
                      :class="{ 'route-picker-row-on': pickerIds.includes(r.value) }"
                      @click="togglePickerRoute(r.value)"
                    >
                      <td class="route-picker-check">
                        <v-checkbox
                          :input-value="pickerIds.includes(r.value)"
                          color="success"
                          hide-details
                          class="route-picker-cb"
                          @click.stop
                          @change="togglePickerRoute(r.value)"
                        ></v-checkbox>
                      </td>
                      <td class="font-weight-bold">#{{ r.busNumber }}</td>
                      <td>{{ r.direction }}</td>
                      <td>{{ r.timeStart }}</td>
                      <td class="caption">{{ r.buildings }}</td>
                      <td class="caption">{{ r.grades }}</td>
                      <td class="caption" :class="{ 'grey--text': !r.hasSchedule }">
                        {{ r.scheduleLabel }}
                      </td>
                    </tr>
                    <tr v-if="!pickerRouteItems.length">
                      <td colspan="7" class="text-xs-center grey--text py-4">
                        No matching routes
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-card-text>
              <v-card-actions class="route-picker-actions">
                <span class="caption grey--text">{{ pickerIds.length }} selected · showing {{ pickerRouteItems.length }} of {{ scheduleRouteItems.length }}</span>
                <v-btn small flat :disabled="!pickerIds.length" @click="pickerIds = []">
                  Clear
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn small flat @click="routePickerOpen = false">Cancel</v-btn>
                <v-btn small color="success" @click="applyRoutePicker">Done</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <div v-if="loadingSchedules" class="text-xs-center py-4 grey--text">
            Loading schedules…
          </div>
          <div v-else-if="!schedules.length" class="text-xs-center py-4 grey--text">
            No schedules yet. Without a schedule, drivers get no notifications.
          </div>
          <template v-else>
            <v-layout row wrap align-center class="schedule-filters mb-2">
              <v-flex xs12 sm3 class="pr-2">
                <v-text-field
                  v-model="scheduleSearch"
                  prepend-inner-icon="mdi-magnify"
                  label="Search"
                  placeholder="Bus, building, grade, or note"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-flex>
              <v-flex v-if="showScheduleBuildingFilter" xs12 sm3 class="pr-2">
                <v-autocomplete
                  v-model="scheduleBuildingFilter"
                  :items="buildings"
                  label="Building"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  hide-details
                  placeholder="All buildings"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 sm3 class="pr-2">
                <v-autocomplete
                  v-model="scheduleGradeFilter"
                  :items="scheduleGradeOptions"
                  label="Grade"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  hide-details
                  placeholder="All grades"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 sm3 class="pr-2">
                <v-autocomplete
                  v-model="scheduleStartFilter"
                  :items="scheduleStartOptions"
                  label="Start time"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  hide-details
                  placeholder="All start times"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
            <div v-if="scheduleFilterActive" class="caption grey--text mb-2">
              {{ filteredSchedules.length }} of {{ schedules.length }}
            </div>
            <div v-if="!filteredSchedules.length" class="text-xs-center py-4 grey--text">
              No schedules match.
            </div>
            <table v-else class="identity-table">
            <thead>
              <tr>
                <th
                  v-for="col in scheduleColumns"
                  :key="col.key"
                  class="schedule-sort"
                  @click="sortSchedules(col.key)"
                >
                  {{ col.label }}
                  <v-icon
                    small
                    class="schedule-sort-icon"
                    :class="{ 'schedule-sort-on': scheduleSort.key === col.key }"
                    >{{
                      scheduleSort.key === col.key && scheduleSort.dir === "desc"
                        ? "mdi-arrow-down"
                        : "mdi-arrow-up"
                    }}</v-icon
                  >
                </th>
                <th class="col-save"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in pagedSchedules"
                :key="s.id"
                :class="{ 'schedule-inactive': !s.isActive }"
              >
                <td>
                  <div v-if="s.routeId" class="schedule-route-option">
                    <div>
                      #{{ scheduleBusNumber(s) || "—" }} · {{ s.direction || "Dropoff" }}
                      <span v-if="s.timeStart"> · {{ s.timeStart }}</span>
                    </div>
                    <div class="caption grey--text">
                      {{ s.buildings || "No building" }}
                      <span v-if="s.grades"> · {{ s.grades }}</span>
                    </div>
                  </div>
                  <span v-else-if="s.building">Building {{ s.building }}</span>
                  <span v-else class="font-weight-bold">Default (all routes)</span>
                </td>
                <td class="caption">{{ formatWeekDays(s.weekDays) }}</td>
                <td>{{ formatTime(s.sendTime) }}</td>
                <td>{{ s.lastSendTime ? formatTime(s.lastSendTime) : "Rest of day" }}</td>
                <td>
                  <v-icon small :color="s.isActive ? 'success' : 'grey'">{{
                    s.isActive ? "mdi-check-circle" : "mdi-pause-circle"
                  }}</v-icon>
                </td>
                <td class="caption">{{ s.notes }}</td>
                <td class="col-save text-no-wrap">
                  <v-btn icon small class="my-0" title="Edit" :disabled="!canManagePresets" @click="editSchedule(s)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small class="my-0" title="Delete" :disabled="!canManagePresets" @click="deleteSchedule(s)">
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
            </table>
            <div v-if="schedulePageCount > 1" class="schedule-pagination">
              <v-pagination
                v-model="schedulePage"
                :length="schedulePageCount"
                :total-visible="7"
                color="success"
              ></v-pagination>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </component>
</template>

<script>
import { buildColorMap, getRouteBusNumber, getRouteTile } from "../busRouteDisplay";
import { routeHasDriver } from "../busChangeRouteFilter";

export default {
  name: "BusRouteIdentityDialog",
  props: {
    value: { type: Boolean, default: false },
    canManagePresets: { type: Boolean, default: false },
    branch: { type: String, default: "" },
    defaultKind: { type: String, default: "Regular" },
    embedded: { type: Boolean, default: false },
    activePanel: { type: Number, default: 0 },
  },
  data: () => ({
    tab: 0,
    editingId: null,
    loading: false,
    routes: [],
    buildings: [],
    colors: [],
    kind: "Regular",
    building: "",
    direction: "",
    search: "",
    newColor: { name: "", hex: "#4CAF50" },
    addingColor: false,
    directionItems: [
      { text: "All", value: "" },
      { text: "Pickup", value: "Pickup" },
      { text: "Dropoff", value: "Dropoff" },
    ],
    schedules: [],
    scheduleSearch: "",
    scheduleBuildingFilter: [],
    scheduleGradeFilter: [],
    scheduleStartFilter: [],
    scheduleSort: { key: "", dir: "asc" },
    scheduleColumns: [
      { key: "applies", label: "Applies to" },
      { key: "days", label: "Days" },
      { key: "sendTime", label: "Send at" },
      { key: "lastSendTime", label: "Keep sending until" },
      { key: "active", label: "Active" },
      { key: "notes", label: "Notes" },
    ],
    schedulePage: 1,
    scheduleRowsPerPage: 8,
    routePickerOpen: false,
    routePickerSearch: "",
    routePickerBuilding: [],
    pickerSort: { key: "", dir: "asc" },
    hideScheduledRoutes: false,
    pickerColFilters: {
      busNumber: "",
      direction: "",
      timeStart: "",
      buildings: "",
      grades: "",
      scheduleLabel: "",
    },
    pickerColumns: [
      { key: "busNumber", label: "Bus" },
      { key: "direction", label: "Direction" },
      { key: "timeStart", label: "Start" },
      { key: "buildings", label: "Buildings" },
      { key: "grades", label: "Grades" },
      { key: "scheduleLabel", label: "Schedule" },
    ],
    pickerIds: [],
    loadingSchedules: false,
    savingSchedule: false,
    scheduleForm: {
      id: 0,
      scope: "building",
      building: "",
      routeIds: [],
      sendTime: "",
      lastSendTime: "",
      weekDays: [0, 1, 2, 3, 4],
      isActive: true,
      notes: "",
    },
    scheduleScopeItems: [
      { text: "Building", value: "building" },
      { text: "Routes", value: "route" },
      { text: "Default (all routes)", value: "default" },
    ],
    weekDayItems: [
      { text: "Sun", value: 0 },
      { text: "Mon", value: 1 },
      { text: "Tue", value: 2 },
      { text: "Wed", value: 3 },
      { text: "Thu", value: 4 },
      { text: "Fri", value: 5 },
      { text: "Sat", value: 6 },
    ],
  }),
  computed: {
    shellProps() {
      if (this.embedded) return {};
      return {
        value: this.value,
        maxWidth: "1000",
        scrollable: true,
      };
    },
    currentPanel() {
      return this.embedded ? this.activePanel : this.tab;
    },
    colorMap() {
      return buildColorMap(this.colors);
    },
    colorItems() {
      return this.colors.map((c) => ({ name: c.name, hex: c.hex }));
    },
    scheduleRouteItems() {
      const scheduleByRoute = {};
      (this.schedules || []).forEach((s) => {
        if (!s.routeId) return;
        const label = this.formatTime(s.sendTime);
        if (!scheduleByRoute[s.routeId]) scheduleByRoute[s.routeId] = [];
        if (label && scheduleByRoute[s.routeId].indexOf(label) === -1) {
          scheduleByRoute[s.routeId].push(label);
        }
      });
      return this.routes
        .filter((r) => r.direction === "Dropoff" || r.direction === "Pickup")
        .map((r) => {
          const busNumber = getRouteBusNumber(r) || "—";
          const timeStart = String(r.timeStart || "").trim();
          const buildings = r.buildings || "";
          const grades = r.grades || "";
          const direction = r.direction || "";
          const times = scheduleByRoute[r.id] || [];
          const hasSchedule = times.length > 0;
          const scheduleLabel = hasSchedule ? times.join(", ") : "None";
          return {
            value: r.id,
            busNumber,
            buildings,
            grades,
            direction,
            timeStart,
            hasSchedule,
            scheduleLabel,
            searchText: [
              busNumber,
              direction,
              timeStart,
              buildings,
              grades,
              scheduleLabel,
              r.description,
              r.season,
            ]
              .filter(Boolean)
              .join(" "),
          };
        })
        .sort((a, b) => {
          // Routes without a bus number go to the bottom.
          const aNone = a.busNumber === "—";
          const bNone = b.busNumber === "—";
          if (aNone !== bNone) return aNone ? 1 : -1;
          return String(a.busNumber).localeCompare(String(b.busNumber), undefined, {
            numeric: true,
          });
        });
    },
    schedulePageCount() {
      return Math.max(
        1,
        Math.ceil(this.filteredSchedules.length / this.scheduleRowsPerPage)
      );
    },
    pagedSchedules() {
      const start = (this.schedulePage - 1) * this.scheduleRowsPerPage;
      return this.sortedSchedules.slice(start, start + this.scheduleRowsPerPage);
    },
    showScheduleBuildingFilter() {
      return this.canManagePresets || (this.buildings || []).length > 1;
    },
    scheduleGradeOptions() {
      const grades = {};
      const add = (value) => {
        this.splitList(value).forEach((grade) => {
          grades[grade.toUpperCase()] = grade;
        });
      };
      (this.schedules || []).forEach((s) => add(s.grades));
      (this.routes || []).forEach((r) => add(r.grades));
      return Object.keys(grades)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((key) => grades[key]);
    },
    scheduleStartOptions() {
      const times = {};
      (this.schedules || []).forEach((s) => {
        const label = this.scheduleStartLabel(s);
        if (label) times[label.toUpperCase()] = label;
      });
      return Object.keys(times)
        .sort((a, b) => this.pickerTimeValue(times[a]) - this.pickerTimeValue(times[b]))
        .map((key) => times[key]);
    },
    scheduleFilterActive() {
      return (
        !!(this.scheduleSearch || "").trim() ||
        (this.showScheduleBuildingFilter && (this.scheduleBuildingFilter || []).length > 0) ||
        (this.scheduleGradeFilter || []).length > 0 ||
        (this.scheduleStartFilter || []).length > 0
      );
    },
    filteredSchedules() {
      const query = (this.scheduleSearch || "").trim().toLowerCase();
      const buildings = this.showScheduleBuildingFilter ? this.scheduleBuildingFilter || [] : [];
      const grades = this.scheduleGradeFilter || [];
      const starts = this.scheduleStartFilter || [];
      return (this.schedules || []).filter((s) => {
        if (!this.scheduleMatchesBuilding(s, buildings)) return false;
        if (!this.scheduleMatchesGrade(s, grades)) return false;
        if (!this.scheduleMatchesStart(s, starts)) return false;
        return !query || this.scheduleSearchText(s).indexOf(query) !== -1;
      });
    },
    sortedSchedules() {
      const items = this.filteredSchedules.slice();
      const key = this.scheduleSort && this.scheduleSort.key;
      if (!key) return items;
      const sign = this.scheduleSort.dir === "desc" ? -1 : 1;
      items.sort((a, b) => this.compareSchedules(a, b, key) * sign);
      return items;
    },
    selectedRouteItems() {
      const ids = this.scheduleForm.routeIds || [];
      return ids
        .map((id) => this.scheduleRouteItems.find((r) => r.value === id))
        .filter(Boolean);
    },
    routePickerBuildingItems() {
      const items = this.buildings.map((b) => ({ text: "Building " + b, value: b }));
      return [{ text: "All buildings", value: "" }].concat(items);
    },
    pickerRouteItems() {
      const q = (this.routePickerSearch || "").trim().toLowerCase();
      const wanted = (this.routePickerBuilding || []).map((b) =>
        String(b).trim().toUpperCase()
      );
      const colFilters = Object.keys(this.pickerColFilters)
        .map((k) => [k, String(this.pickerColFilters[k] || "").trim().toLowerCase()])
        .filter((x) => x[1]);
      const items = this.scheduleRouteItems.filter((r) => {
        if (this.hideScheduledRoutes && r.hasSchedule) return false;
        if (wanted.length) {
          const list = String(r.buildings || "")
            .split(",")
            .map((b) => b.trim().toUpperCase());
          if (!wanted.some((b) => list.includes(b))) return false;
        }
        for (const [k, v] of colFilters) {
          if (String(r[k] || "").toLowerCase().indexOf(v) === -1) return false;
        }
        return !q || r.searchText.toLowerCase().indexOf(q) !== -1;
      });
      const { key, dir } = this.pickerSort;
      if (!key) return items;
      const sign = dir === "desc" ? -1 : 1;
      return items.slice().sort((a, b) => {
        let av = a[key];
        let bv = b[key];
        if (key === "timeStart") {
          av = this.pickerTimeValue(av);
          bv = this.pickerTimeValue(bv);
          return (av - bv) * sign;
        }
        return (
          String(av || "").localeCompare(String(bv || ""), undefined, { numeric: true }) * sign
        );
      });
    },
    allPickerRoutesSelected() {
      return (
        this.pickerRouteItems.length > 0 &&
        this.pickerRouteItems.every((r) => this.pickerIds.includes(r.value))
      );
    },
    canSaveSchedule() {
      const f = this.scheduleForm;
      if (!f.sendTime || !f.weekDays.length) return false;
      if (f.scope === "building" && !f.building) return false;
      if (f.scope === "route" && !(f.routeIds || []).length) return false;
      return true;
    },
    buildingItems() {
      const items = this.buildings.map((b) => ({ text: b, value: b }));
      // Branch users only see their own buildings. Admins and Transportation
      // also get every building at once.
      if (!this.canManagePresets) return items;
      return [{ text: "All buildings", value: "" }].concat(items);
    },
    visibleRoutes() {
      const q = (this.search || "").trim().toLowerCase();
      const kind = this.kind.toLowerCase();
      const building = (this.building || "").toUpperCase();
      return this.routes.filter((r) => {
        if (!routeHasDriver(r)) return false;
        if (r.kindKey !== kind) return false;
        if (this.direction && r.direction !== this.direction) return false;
        if (building && !r.buildingKeys.includes(building)) return false;
        return !q || r.searchText.indexOf(q) !== -1;
      });
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    tab(t) {
      if (!this.embedded && t === 2) this.loadSchedules();
    },
    activePanel(t) {
      if (this.embedded && t === 2) this.loadSchedules();
    },
    schedulePageCount(count) {
      if (this.schedulePage > count) this.schedulePage = count || 1;
    },
    scheduleSearch() {
      this.schedulePage = 1;
    },
    scheduleBuildingFilter: {
      handler() {
        this.schedulePage = 1;
      },
      deep: true,
    },
    scheduleGradeFilter: {
      handler() {
        this.schedulePage = 1;
      },
      deep: true,
    },
    scheduleStartFilter: {
      handler() {
        this.schedulePage = 1;
      },
      deep: true,
    },
  },
  async mounted() {
    if (!this.value) return;
    await this.load();
    if (this.currentPanel === 2) this.loadSchedules();
  },
  methods: {
    onShellInput(v) {
      if (!this.embedded && !v) this.close();
    },
    close() {
      this.$emit("input", false);
    },
    // Tile and search text are cached on the row so filtering doesn't recompute them.
    buildingLabel(row) {
      return (row.buildingKeys || []).join(", ");
    },
    showBuildingOnIdentity(row) {
      const names = row.buildingNames || [];
      const rows = row.identityRows || [];
      if (!this.canManagePresets) return rows.length > 1;
      return rows.length > 1 || names.length > rows.length;
    },
    buildIdentityRows(row) {
      const names = row.buildingNames || [];
      const allowed = (this.buildings || []).map((b) =>
        String(b)
          .trim()
          .toUpperCase()
      );
      const visible = this.canManagePresets
        ? names
        : names.filter((b) => allowed.includes(b.toUpperCase()));
      const use = this.canManagePresets ? names : visible;
      if (!use.length) {
        if (names.length || !this.canManagePresets) return [];
        const room = row.roomNumber || "";
        const color = row.chiderColor || null;
        return [
          {
            building: "",
            roomNumber: room,
            chiderColor: color,
            editRoom: room,
            editColor: color,
          },
        ];
      }
      const list = row.identities || [];
      return use.map((building) => {
        const found = list.find(
          (item) =>
            String(item.building || "")
              .trim()
              .toUpperCase() === building.toUpperCase()
        );
        const fallback = names.length <= 1;
        const room = found
          ? found.roomNumber || ""
          : fallback
            ? row.roomNumber || ""
            : "";
        const color = found
          ? found.chiderColor || null
          : fallback
            ? row.chiderColor || null
            : null;
        return {
          building,
          roomNumber: room,
          chiderColor: color,
          editRoom: room,
          editColor: color,
        };
      });
    },
    tileRoute(row) {
      const first = (row.identityRows || [])[0];
      if (!first) return row;
      const editing = this.editingId === row.id;
      return {
        ...row,
        roomNumber: editing ? first.editRoom : first.roomNumber,
        chiderColor: editing ? first.editColor : first.chiderColor,
      };
    },
    refreshRow(row) {
      row.tile = getRouteTile(this.tileRoute(row), this.colorMap);
      const identityText = (row.identityRows || [])
        .map((b) => [b.building, b.roomNumber, b.chiderColor].join(" "))
        .join(" ");
      row.searchText = [
        row.busNumber,
        row.roomNumber,
        row.chiderColor,
        identityText,
        row.grades,
        row.buildings,
        row.driver,
        row.description,
      ]
        .join(" ")
        .toLowerCase();
    },
    rowTile(row) {
      if (this.editingId !== row.id) return row.tile;
      return getRouteTile(this.tileRoute(row), this.colorMap);
    },
    startEdit(row) {
      const current = this.routes.find((r) => r.id === this.editingId);
      if (current && current !== row) this.cancelEdit(current);
      this.editingId = row.id;
    },
    cancelEdit(row) {
      (row.identityRows || []).forEach((b) => {
        b.editRoom = b.roomNumber || "";
        b.editColor = b.chiderColor || null;
      });
      this.refreshRow(row);
      if (this.editingId === row.id) this.editingId = null;
    },
    isDirty(row) {
      return (row.identityRows || []).some(
        (b) =>
          (b.editRoom || "").trim() !== (b.roomNumber || "").trim() ||
          (b.editColor || "") !== (b.chiderColor || "")
      );
    },
    notify(text) {
      this.$notify({ text, group: "notification", type: "success" });
    },
    async load() {
      this.kind = this.defaultKind || "Regular";
      this.loading = true;
      try {
        await this.loadColors();
        const res = await this.$http.get(
          "api/busRouteIdentity/GetRoutes?branch=" +
            encodeURIComponent(this.branch || "") +
            "&building="
        );
        this.buildings = (res && res.buildings) || [];
        if (
          !this.canManagePresets &&
          this.buildings.length &&
          !this.buildings.includes(this.building)
        ) {
          this.building = this.buildings[0];
        }
        this.editingId = null;
        this.routes = ((res && res.routes) || []).map((r) => {
          const row = {
            ...r,
            editRoom: r.roomNumber || "",
            editColor: r.chiderColor || null,
            saving: false,
            busNumber: getRouteBusNumber(r),
            kindKey: (r.kind || "Regular").trim().toLowerCase(),
            buildingNames: String(r.buildings || "")
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean),
            buildingKeys: String(r.buildings || "")
              .split(",")
              .map((b) => b.trim().toUpperCase())
              .filter(Boolean),
            tile: null,
            searchText: "",
          };
          row.identityRows = this.buildIdentityRows(row);
          this.refreshRow(row);
          return row;
        });
        if (this.building && !this.buildings.includes(this.building)) {
          this.building = "";
        }
      } catch (e) {
        this.routes = [];
      } finally {
        this.loading = false;
      }
    },
    async loadColors() {
      const colors = await this.$http.get("api/busRouteIdentity/GetColors");
      this.colors = (Array.isArray(colors) ? colors : []).map((c) => ({
        ...c,
        editName: c.name,
        editHex: c.hex,
        saving: false,
      }));
      this.routes.forEach((r) => this.refreshRow(r));
    },
    async saveRoute(row) {
      row.saving = true;
      try {
        const rows = row.identityRows || [];
        const hasBuilding = rows.some((b) => String(b.building || "").trim());
        const saved = await this.$http.post(
          "api/busRouteIdentity/UpdateRoute",
          hasBuilding
            ? {
                id: row.id,
                buildings: rows.map((b) => ({
                  building: b.building,
                  roomNumber: (b.editRoom || "").trim(),
                  chiderColor: b.editColor || "",
                })),
              }
            : {
                id: row.id,
                roomNumber: (rows[0] && rows[0].editRoom) || "",
                chiderColor: (rows[0] && rows[0].editColor) || "",
              }
        );
        row.roomNumber =
          saved.roomNumber != null ? saved.roomNumber : row.roomNumber || "";
        row.chiderColor =
          saved.chiderColor != null ? saved.chiderColor : null;
        row.identities = saved.identities || row.identities || [];
        row.identityRows = this.buildIdentityRows(row);
        this.refreshRow(row);
        if (this.editingId === row.id) this.editingId = null;
        this.$emit("saved");
        this.notify("Route saved.");
      } catch (e) {
        // error toast comes from the http plugin
      } finally {
        row.saving = false;
      }
    },
    async addColor() {
      if (!this.newColor.name) return;
      this.addingColor = true;
      try {
        await this.$http.post("api/busRouteIdentity/AddColor", {
          name: this.newColor.name.trim(),
          hex: this.newColor.hex,
        });
        this.newColor = { name: "", hex: "#4CAF50" };
        await this.loadColors();
        this.$emit("saved");
      } catch (e) {
        // error toast comes from the http plugin
      } finally {
        this.addingColor = false;
      }
    },
    async saveColor(c) {
      c.saving = true;
      try {
        await this.$http.post("api/busRouteIdentity/UpdateColor", {
          id: c.id,
          name: (c.editName || "").trim(),
          hex: c.editHex,
        });
        const oldName = c.name;
        await this.loadColors();
        const newName = c.editName.trim();
        this.routes.forEach((r) => {
          if (r.chiderColor === oldName) r.chiderColor = newName;
          if (r.editColor === oldName) r.editColor = newName;
          (r.identityRows || []).forEach((b) => {
            if (b.chiderColor === oldName) b.chiderColor = newName;
            if (b.editColor === oldName) b.editColor = newName;
          });
          (r.identities || []).forEach((b) => {
            if (b.chiderColor === oldName) b.chiderColor = newName;
          });
          this.refreshRow(r);
        });
        this.$emit("saved");
      } catch (e) {
        // error toast comes from the http plugin
      } finally {
        c.saving = false;
      }
    },
    splitList(value) {
      return String(value || "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    },
    scheduleMatchesBuilding(schedule, wanted) {
      if (!wanted.length) return true;
      if (!schedule.routeId && !schedule.building) return true;
      const selected = wanted.map((b) => String(b).trim().toUpperCase());
      if (
        schedule.building &&
        selected.indexOf(String(schedule.building).trim().toUpperCase()) !== -1
      ) {
        return true;
      }
      return this.splitList(schedule.buildings).some(
        (b) => selected.indexOf(b.toUpperCase()) !== -1
      );
    },
    scheduleMatchesGrade(schedule, wanted) {
      if (!wanted.length) return true;
      if (!schedule.routeId) return true;
      const selected = wanted.map((g) => String(g).trim().toUpperCase());
      return this.splitList(schedule.grades).some(
        (g) => selected.indexOf(g.toUpperCase()) !== -1
      );
    },
    scheduleStartLabel(schedule) {
      const raw = String((schedule && schedule.timeStart) || "").trim();
      if (!raw) return "";
      const minutes = this.pickerTimeValue(raw);
      if (minutes === 99999) return raw;
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      const suffix = h >= 12 ? "PM" : "AM";
      const hour = h % 12 === 0 ? 12 : h % 12;
      return hour + ":" + String(m).padStart(2, "0") + " " + suffix;
    },
    scheduleMatchesStart(schedule, wanted) {
      if (!wanted.length) return true;
      const label = this.scheduleStartLabel(schedule);
      if (!label) return false;
      const selected = wanted.map((t) => String(t).trim().toUpperCase());
      return selected.indexOf(label.toUpperCase()) !== -1;
    },
    sortSchedules(key) {
      if (this.scheduleSort.key !== key) {
        this.scheduleSort = { key, dir: "asc" };
      } else if (this.scheduleSort.dir === "asc") {
        this.scheduleSort = { key, dir: "desc" };
      } else {
        this.scheduleSort = { key: "", dir: "asc" };
      }
      this.schedulePage = 1;
    },
    compareSchedules(a, b, key) {
      if (key === "applies") {
        const aBus = a.routeId ? this.scheduleBusNumber(a) : "";
        const bBus = b.routeId ? this.scheduleBusNumber(b) : "";
        if (!aBus !== !bBus) return aBus ? -1 : 1;
        const bus = String(aBus).localeCompare(String(bBus), undefined, { numeric: true });
        if (bus) return bus;
        return String(a.direction || a.building || "").localeCompare(
          String(b.direction || b.building || "")
        );
      }
      if (key === "days") {
        return String(a.weekDays || "0,1,2,3,4,5,6").localeCompare(
          String(b.weekDays || "0,1,2,3,4,5,6")
        );
      }
      if (key === "sendTime") {
        return this.pickerTimeValue(a.sendTime) - this.pickerTimeValue(b.sendTime);
      }
      if (key === "lastSendTime") {
        const aTime = a.lastSendTime ? this.pickerTimeValue(a.lastSendTime) : 24 * 60;
        const bTime = b.lastSendTime ? this.pickerTimeValue(b.lastSendTime) : 24 * 60;
        return aTime - bTime;
      }
      if (key === "active") {
        return (a.isActive ? 0 : 1) - (b.isActive ? 0 : 1);
      }
      return String(a.notes || "").localeCompare(String(b.notes || ""));
    },
    scheduleSearchText(schedule) {
      const parts = [
        schedule.routeId ? "#" + (this.scheduleBusNumber(schedule) || "") : "",
        schedule.direction,
        schedule.timeStart,
        schedule.buildings,
        schedule.grades,
        schedule.building ? "Building " + schedule.building : "",
        !schedule.routeId && !schedule.building ? "Default all routes" : "",
        schedule.notes,
        this.formatTime(schedule.sendTime),
        schedule.lastSendTime ? this.formatTime(schedule.lastSendTime) : "Rest of day",
        this.formatWeekDays(schedule.weekDays),
      ];
      return parts
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    },
    scheduleBusNumber(schedule) {
      if (!schedule || !schedule.routeId) return "";
      const route = (this.routes || []).find((r) => r.id === schedule.routeId);
      const fromRoute = getRouteBusNumber(route);
      if (fromRoute) return fromRoute;
      return String(schedule.busNumber || "").trim();
    },
    formatTime(value) {
      const [h, m] = String(value || "").split(":").map((v) => parseInt(v, 10));
      if (isNaN(h) || isNaN(m)) return value || "";
      const suffix = h >= 12 ? "PM" : "AM";
      const hour = h % 12 === 0 ? 12 : h % 12;
      return hour + ":" + String(m).padStart(2, "0") + " " + suffix;
    },
    formatWeekDays(value) {
      if (!value) return "Every day";
      return String(value)
        .split(",")
        .map((d) => {
          const item = this.weekDayItems.find((w) => w.value === parseInt(d, 10));
          return item ? item.text : d;
        })
        .join(", ");
    },
    sortPicker(key) {
      if (this.pickerSort.key !== key) {
        this.pickerSort = { key, dir: "asc" };
      } else if (this.pickerSort.dir === "asc") {
        this.pickerSort = { key, dir: "desc" };
      } else {
        this.pickerSort = { key: "", dir: "asc" };
      }
    },
    pickerTimeValue(text) {
      const m = String(text || "").match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!m) return 99999;
      let h = parseInt(m[1], 10) % 12;
      if ((m[3] || "").toUpperCase() === "PM") h += 12;
      if (!m[3] && parseInt(m[1], 10) >= 12) h = parseInt(m[1], 10);
      return h * 60 + parseInt(m[2], 10);
    },
    openRoutePicker() {
      this.pickerIds = (this.scheduleForm.routeIds || []).slice();
      this.routePickerSearch = "";
      this.routePickerBuilding = [];
      this.pickerSort = { key: "", dir: "asc" };
      this.hideScheduledRoutes = false;
      this.pickerColFilters = {
        busNumber: "",
        direction: "",
        timeStart: "",
        buildings: "",
        grades: "",
        scheduleLabel: "",
      };
      this.routePickerOpen = true;
    },
    togglePickerRoute(id) {
      this.pickerIds = this.pickerIds.includes(id)
        ? this.pickerIds.filter((x) => x !== id)
        : this.pickerIds.concat([id]);
    },
    togglePickerAll(on) {
      const visible = this.pickerRouteItems.map((r) => r.value);
      this.pickerIds = on
        ? Array.from(new Set(this.pickerIds.concat(visible)))
        : this.pickerIds.filter((id) => !visible.includes(id));
    },
    applyRoutePicker() {
      this.scheduleForm.routeIds = this.pickerIds.slice();
      this.routePickerOpen = false;
    },
    removeScheduleRoute(id) {
      this.scheduleForm.routeIds = (this.scheduleForm.routeIds || []).filter(
        (x) => x !== id
      );
    },
    toggleWeekDay(day) {
      const days = this.scheduleForm.weekDays;
      this.scheduleForm.weekDays = days.includes(day)
        ? days.filter((d) => d !== day)
        : days.concat([day]).sort((a, b) => a - b);
    },
    resetScheduleForm() {
      this.scheduleForm = {
        id: 0,
        scope: "building",
        building: "",
        routeIds: [],
        sendTime: "",
        lastSendTime: "",
        weekDays: [0, 1, 2, 3, 4],
        isActive: true,
        notes: "",
      };
    },
    editSchedule(s) {
      this.scheduleForm = {
        id: s.id,
        scope: s.routeId ? "route" : s.building ? "building" : "default",
        building: s.building || "",
        routeIds: s.routeId ? [s.routeId] : [],
        sendTime: s.sendTime || "",
        lastSendTime: s.lastSendTime || "",
        weekDays: s.weekDays
          ? String(s.weekDays)
              .split(",")
              .map((d) => parseInt(d, 10))
          : [0, 1, 2, 3, 4, 5, 6],
        isActive: s.isActive,
        notes: s.notes || "",
      };
    },
    async loadSchedules() {
      this.loadingSchedules = true;
      try {
        const res = await this.$http.get(
          "api/busRouteIdentity/GetDriverSmsSchedules"
        );
        this.schedules = Array.isArray(res) ? res : [];
      } catch (e) {
        this.schedules = [];
      } finally {
        this.loadingSchedules = false;
      }
    },
    async saveSchedule() {
      const f = this.scheduleForm;
      const routeIds =
        f.scope === "route" ? (f.routeIds || []).filter(Boolean) : [];
      this.savingSchedule = true;
      try {
        const posts = [];
        if (f.scope !== "route") {
          posts.push({
            id: f.id || 0,
            routeID: null,
            building: f.scope === "building" ? f.building : "",
          });
        } else if (f.id) {
          const original = this.schedules.find((s) => s.id === f.id);
          const originalRouteId = original && original.routeId;
          const updateRouteId = routeIds.includes(originalRouteId)
            ? originalRouteId
            : routeIds[0];
          posts.push({ id: f.id, routeID: updateRouteId, building: "" });
          routeIds
            .filter((routeId) => routeId !== updateRouteId)
            .forEach((routeId) => {
              const existing = this.schedules.find((s) => s.routeId === routeId);
              posts.push({
                id: existing ? existing.id : 0,
                routeID: routeId,
                building: "",
              });
            });
        } else {
          routeIds.forEach((routeId) => {
            const existing = this.schedules.find((s) => s.routeId === routeId);
            posts.push({
              id: existing ? existing.id : 0,
              routeID: routeId,
              building: "",
            });
          });
        }

        for (let i = 0; i < posts.length; i++) {
          await this.$http.post("api/busRouteIdentity/SaveDriverSmsSchedule", {
            id: posts[i].id,
            building: posts[i].building,
            routeID: posts[i].routeID,
            sendTime: f.sendTime,
            lastSendTime: f.lastSendTime || "",
            weekDays: f.weekDays.join(","),
            isActive: f.isActive,
            notes: f.notes,
          });
        }
        this.resetScheduleForm();
        await this.loadSchedules();
        this.notify(
          posts.length > 1 ? posts.length + " schedules saved." : "Schedule saved."
        );
      } catch (e) {
        // error toast comes from the http plugin
      } finally {
        this.savingSchedule = false;
      }
    },
    async deleteSchedule(s) {
      if (!confirm("Delete this schedule?")) return;
      try {
        await this.$http.post(
          "api/busRouteIdentity/DeleteDriverSmsSchedule?id=" + s.id
        );
        await this.loadSchedules();
      } catch (e) {
        // error toast comes from the http plugin
      }
    },
    async deleteColor(c) {
      if (!confirm("Delete " + c.name + "?")) return;
      try {
        await this.$http.post("api/busRouteIdentity/DeleteColor?id=" + c.id);
        await this.loadColors();
        this.$emit("saved");
      } catch (e) {
        // error toast comes from the http plugin
      }
    },
  },
};
</script>

<style scoped>
/* Same look as the toggle in Grades Setup */
.identity-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.identity-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.identity-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.identity-body {
  background: #f4f6f4;
  min-height: 300px;
}

.schedule-pagination {
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
}

.identity-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 8px;
}

.identity-table th {
  text-align: left;
  font-size: 12px;
  color: #616161;
  padding: 6px 8px;
  border-bottom: 1px solid #e0e0e0;
}

.schedule-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.schedule-sort-icon {
  opacity: 0.25;
  vertical-align: middle;
}

.schedule-sort:hover .schedule-sort-icon,
.schedule-sort-icon.schedule-sort-on {
  opacity: 1;
  color: #388e3c;
}

.identity-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.col-tile {
  width: 64px;
}

.col-room {
  width: 160px;
}

.identity-building-line {
  display: flex;
  align-items: center;
  min-height: 28px;
}

.identity-building-line + .identity-building-line {
  margin-top: 4px;
}

.identity-building-name {
  flex: 0 0 auto;
  min-width: 36px;
  margin-right: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #757575;
}

.col-color {
  width: 200px;
}

.col-save {
  width: 80px;
}

.color-swatch-lg {
  width: 22px;
  height: 22px;
}

.color-name {
  font-size: 14px;
}

.presets-locked {
  opacity: 0.6;
  pointer-events: none;
}

.route-picker-card {
  display: flex;
  flex-direction: column;
}

.route-picker-title {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  margin-bottom: 14px;
  color: #fff;
  background: linear-gradient(135deg, #43a047, #2e7d32);
}

.route-picker-heading {
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
}

.route-picker-subheading {
  font-size: 12px;
  opacity: 0.85;
}

.route-picker-filters {
  display: flex;
  align-items: flex-end;
  padding: 0 20px 12px;
}

.route-picker-search {
  flex: 1 1 auto;
}

.route-picker-hide {
  flex: 0 0 auto;
  white-space: nowrap;
}

.route-picker-building {
  flex: 0 0 280px;
}

.route-picker-cb {
  margin: 0;
  padding: 0;
  justify-content: center;
}

.route-picker-cb >>> .v-input__slot {
  margin: 0;
  justify-content: center;
}

.route-picker-cb >>> .v-input--selection-controls__input {
  margin: 0;
}

.route-picker-table td {
  padding-top: 6px;
  padding-bottom: 6px;
}

.route-picker-table thead th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #616161;
}

.route-picker-body {
  max-height: 55vh;
  overflow-y: auto;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.route-picker-table thead th {
  position: sticky;
  top: 0;
  background: #fafafa;
  z-index: 1;
}

.route-picker-row {
  cursor: pointer;
}

.route-picker-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.route-picker-sort-icon {
  opacity: 0;
  font-size: 14px !important;
}

.route-picker-sort:hover .route-picker-sort-icon {
  opacity: 0.4;
}

.route-picker-sort-icon.route-picker-sort-on {
  opacity: 1 !important;
  color: #2e7d32;
}

.route-picker-colfilter {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: normal;
  text-transform: none;
  letter-spacing: 0;
  color: #212121;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  outline: none;
}

.route-picker-colfilter:focus {
  border-color: #4caf50;
}

.route-picker-row:hover {
  background: #f5f5f5;
}

.route-picker-row-on,
.route-picker-row-on:hover {
  background: #e8f5e9;
  box-shadow: inset 3px 0 0 #4caf50;
}

.route-picker-check {
  width: 40px;
  text-align: center;
}

.route-picker-actions {
  padding: 8px 16px;
}

.schedule-route-option {
  line-height: 1.3;
  padding: 4px 0;
}

.identity-tile {
  min-width: 48px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  border: 1px solid transparent;
}

.identity-tile-mono {
  border: 2px solid #111111;
}

.color-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #bdbdbd;
  vertical-align: middle;
}

.color-input {
  width: 40px;
  height: 32px;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
}

.color-row {
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 6px;
  padding: 2px 8px;
  margin-bottom: 4px;
}

.color-hex {
  width: 70px;
}

.color-add-row {
  background: #ffffff;
  border-radius: 6px;
  padding: 6px 8px;
}

.schedule-editor {
  background: #ffffff;
  border-radius: 6px;
  padding: 8px 10px;
}

.weekday-toggle {
  display: inline-flex;
  border: 1px solid #c8d6c8;
  border-radius: 6px;
  overflow: hidden;
}

.weekday-btn {
  padding: 4px 9px;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
  background: #ffffff;
  border-right: 1px solid #e0e0e0;
}

.weekday-btn:last-child {
  border-right: none;
}

.weekday-btn-on {
  background: #388e3c;
  color: #ffffff;
}

.schedule-active {
  flex: 0 0 auto;
}

.schedule-inactive td {
  color: #9e9e9e;
}

.admin-only-panel {
  max-width: 640px;
  margin: 8px auto;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 22px 24px 8px;
  text-align: center;
}

.admin-only-icon {
  font-size: 36px !important;
}

.admin-only-title {
  font-size: 16px;
  font-weight: 600;
  color: #424242;
  margin: 8px 0 12px;
}

.admin-only-panel p {
  text-align: left;
  font-size: 14px;
  line-height: 1.45;
  color: #616161;
  margin-bottom: 10px;
}
</style>
